import { VERTICAL_KEYS } from '@nexofacil/schemas';
import type { ComponentType } from 'react';
import { DeibianeLanding } from './deibiane';
import { Padrao2Landing as AngelLanding } from './angel/padrao2';
import { AngelicaLanding } from './angelica';
import { MCMVLanding } from './mcmv';
import { PVLanding } from './pv';
import type { LandingProps } from './types';

// =============================================================================
// Resolve qual componente de landing renderizar pra um broker. Estratégia
// híbrida:
//
//   1. BROKER_OVERRIDES — match exato por subdomain. Vazio hoje. Use só
//      quando um cliente premium pediu design 100% único e a manutenção
//      separada compensa.
//   2. VERTICAL_LANDINGS — match por verticalKey. Todos os brokers da mesma
//      vertical caem aqui se não houver override de cliente.
//   3. Fallback final: MCMVLanding (mantém comportamento histórico pra
//      brokers legados sem verticalKey).
// =============================================================================

type LandingComponent = ComponentType<LandingProps>;

const BROKER_OVERRIDES: Record<string, LandingComponent> = {
  // Deibiane Cavalcante · MCMV com identidade visual própria (paleta
  // Marsala/Nude/Fendi + serifa Playfair). Override de subdomain pra
  // entregar landing dedicada sem afetar Angel/Wesley que continuam
  // no MCMVLanding padrão.
  deibiane: DeibianeLanding,
  // Angélica Coelho · corretora (vertical imobiliario-geral), identidade
  // verde + dourado. Subdomínio "ac" desde 10/10/2026 (antes "angelica",
  // que o index.html redireciona; e "maislar", a marca +Lar, até 09/10/2026).
  ac: AngelicaLanding,
  // Angel Soares · MCMV, "Padrão 2" escolhida em 10/10/2026 (pergunta de
  // renda no topo, faixas do MCMV, Floratta em destaque).
  angelsoares: AngelLanding,
};

/**
 * Campos do "Minha página" que cada landing dedicada lê. O painel esconde os
 * outros pra corretora não editar algo que não muda nada na página dela.
 * Sem entrada = landing padrão, que lê tudo.
 */
export type EditablePageField = 'heroHeadline' | 'heroSubheadline' | 'theme' | 'facebook';
export const DEDICATED_LANDING_FIELDS: Record<string, ReadonlyArray<EditablePageField>> = {
  deibiane: ['heroHeadline', 'heroSubheadline'],
  ac: ['heroSubheadline'],
  // A dela lê só o "Sobre mim" e o Instagram.
  angelsoares: [],
};

const VERTICAL_LANDINGS: Record<string, LandingComponent> = {
  [VERTICAL_KEYS.MCMV_IMOBILIARIO]: MCMVLanding,
  [VERTICAL_KEYS.PROTECAO_VEICULAR]: PVLanding,
};

export function resolveLandingComponent(
  subdomain: string,
  verticalKey: string | undefined,
): LandingComponent {
  const override = BROKER_OVERRIDES[subdomain];
  if (override) return override;
  if (verticalKey && VERTICAL_LANDINGS[verticalKey]) {
    return VERTICAL_LANDINGS[verticalKey];
  }
  return MCMVLanding;
}
